import { FilterModel } from '@ag-grid-community/core/dist/esm/es6/interfaces/iFilter';
import { IServerSideGetRowsParams } from '@ag-grid-community/core/dist/esm/es6/interfaces/iServerSideDatasource';
import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { ApolloLink } from '@apollo/client/core';
import { environment } from '@env/environment';
import { Apollo, ApolloBase, gql } from 'apollo-angular';
import { DynamicForm } from 'dynamic-form/interfaces/dynamic-form-elements.interface';
import { Observable, map } from 'rxjs';
import { BrandRestrictiveUiType } from 'settings/enums/brand-restrictive-ui.enum';
import { Brand } from 'settings/interfaces/brand.interface';
import { BypassErrorLink } from 'shared/abstract/bypass-error-link';
import { GridService } from 'shared/interfaces/grid-service';
import { CachingService } from 'shared/services';

const BRAND_SETTINGS_ENDPOINT = environment.BASE_URL + 'listingApi/settings/brand-settings';

@Injectable({
    providedIn: 'root',
})
export class BrandsService implements GridService {
    private apollo: ApolloBase;

    constructor(
        private apolloProvider: Apollo,
        private cachingService: CachingService,
        private http: HttpClient,
    ) {
        this.apollo = this.apolloProvider.use('suppliersAPI');
    }

    NAME = 'BrandsGridService';

    isDisabled = signal<boolean>(false);

    public createBrand(brand: Brand): Observable<any> {
        const createBrandMutation = `
                mutation createBrand {
                    brands {
                        createBrand (createBrand: {
                            name: "${brand.name}",
                            code: "${brand.code}",
                            listingTypeId: ${brand.listingTypeId},
                            contentDistributionCategory: ${brand.contentDistributionCategory},
                            ${brand.contentDistributionRestrictionsInput ? 'contentDistributionRestrictionsInput: [' + brand.contentDistributionRestrictionsInput.map((id) => `{channelId:"${id}"}`) + '],' : ''}
                            ${brand.visualsDistributionCategory ? 'visualsDistributionCategory:' + brand.visualsDistributionCategory + ',' : ''}
                            ${brand.visualsDistributionRestrictionsInput ? 'visualsDistributionRestrictionsInput: [' + brand.visualsDistributionRestrictionsInput.map((id) => `{channelId:"${id}"}`) + '],' : ''}
                            ${brand.brandDefaultChannels ? 'brandDefaultChannelsInput: [' + brand.brandDefaultChannels.map((id) => `{channelId:"${id}"}`) + '],' : ''}
                            supplierId: "${brand.supplierId}",
                            ${brand.mappings?.length !== 0 ? 'brandMappingsInput: ' + JSON.stringify(brand.mappings).replace(/"([^"]+)":/g, '$1:') + ',' : ''}
                            brandFeatureGroupSettingsInput: [${brand.featureGroupIds.map((id) => `{featureGroupId: "${id}"}`)}],
                            ${
                                brand.hotelCategories.length
                                    ? `brandFeatureDetailsInput:[
                                {resourceName:"hotelCategory", resourceValues:[${brand.hotelCategories?.map((hc) => `"${hc}",`)}]},
                                {resourceName:"segment",resourceValues:["${brand.brandSegment}"]}
                            ]`
                                    : ''
                            }
                            ${
                                brand.brandViewSettings?.type === BrandRestrictiveUiType.DEFAULT
                                    ? 'brandViewSettingsInput: { type: DEFAULT },'
                                    : brand.brandViewSettings?.type === BrandRestrictiveUiType.RESTRICTIVE &&
                                        brand.brandViewSettings.brandViewSettingsChannelRestriction
                                      ? 'brandViewSettingsInput: { type: RESTRICTIVE, channelRestrictionsInput: [' +
                                        brand.brandViewSettings.brandViewSettingsChannelRestriction
                                            .filter((c) => c !== undefined)
                                            .map((c) => `{ channelId: "${c.channelId}" }`)
                                            .join(',') +
                                        '] },'
                                      : ''
                            }
                        })
                        {
                            data {
                                id,
                                name
                            }
                        }
                    }
                }
            `;

        return this.apollo
            .mutate({
                mutation: gql(createBrandMutation),
            })
            .pipe(
                map((response: any) => {
                    return response.data.brands.createBrand.data[0];
                }),
            );
    }

    public getRows(params: IServerSideGetRowsParams, pageNumber: number, pageSize: number): Observable<any> {
        return this.apollo
            .query({
                fetchPolicy: 'no-cache',
                query: gql`
                query getBrands ($orderBy: String, $orderDirection: OrderDirection, $companyName: String, $brandName: String, $brandCode: String) {
                    brands {
                        searchBrands(
                            searchQuery: {
                                pageNumber: ${pageNumber},
                                pageSize: ${pageSize},
                                orderBy: $orderBy,
                                orderDirection: $orderDirection
                            },
                            filtering: {
                                companyName: $companyName,
                                name: $brandName,
                                code: $brandCode
                            }
                        ) {
                            data {
                                id,
                                supplierId,
                                supplier {
                                    companyName
                                },
                                name,
                                code,
                                contentDistributionCategory,
                                visualsDistributionCategory,
                            }
                            totalCount
                        }
                    }
                }
            `,
                variables: {
                    orderBy:
                        params.request.sortModel[0]?.colId && params.request.sortModel[0]?.colId === 'supplier.companyName'
                            ? 'companyName'
                            : (params.request.sortModel[0]?.colId ?? 'name'),
                    orderDirection: params.request.sortModel[0]?.sort.toUpperCase() ?? 'ASC',
                    brandName: (params.request.filterModel as FilterModel)['name']?.filter,
                    brandCode: (params.request.filterModel as FilterModel)['code']?.filter,
                    companyName: (params.request.filterModel as FilterModel)['supplier.companyName']?.filter,
                },
            })
            .pipe(
                map((response: any) => {
                    return response.data.brands.searchBrands;
                }),
            );
    }

    public getBrandById(brandId: string): Observable<any> {
        return this.apollo
            .query({
                query: gql`
                query getBrandById {
                    brands {
                        searchBrands(filtering: {searchTerm: "${brandId}"}) {
                            data {
                                brandDistributionSettings {
                                    brandDistributionRestrictions {
                                        channelId
                                    }
                                    distributionCategory
                                    id
                                    restrictionType
                                }
                                brandFeatureDetails {
                                    resourceName
                                    resourceValues
                                }
                                brandFeatureGroupSettings {
                                    featureGroupId
                                }
                                brandMappings {
                                    mappingType
                                    mappingValue
                                }
                                code
                                contentDistributionCategory
                                brandDefaultChannels {
                                    channelId
                                }
                                entityTracking {
                                    createdAt
                                    createdBy
                                    modifiedAt
                                    modifiedBy
                                }
                                id
                                listingTypeId
                                name
                                supplier {
                                    id
                                    exportsToIcePortal
                                    sharedIdentifier
                                    hasVisualsUIEnabled
                                }
                                visualsDistributionCategory
                                brandLevelEditInProgress
                                brandViewSettings {
                                    type
                                    brandViewSettingsChannelRestriction {
                                        channelId,
                                    }
                                }
                            }
                        }
                    }
                }
            `,
                fetchPolicy: 'no-cache',
            })
            .pipe(
                map((response: any) => {
                    return response.data.brands.searchBrands.data[0];
                }),
            );
    }

    public deleteBrand(brandId: string) {
        const deleteMutation = `
                mutation removeBrand {
                    brands {
                        removeBrand (removeBrand: {id: "${brandId}"}) {
                            statusCode,
                            message
                        }
                    }
                }
        `;

        return this.apollo.mutate({
            mutation: gql(deleteMutation),
            context: {
                link: ApolloLink.from([new BypassErrorLink([400])]),
            },
        });
    }

    public updateBrand(brand: Brand): Observable<any> {
        const brandMutation = `
                mutation updateBrand {
                    brands {
                        updateBrand (
                            updateBrand: {
                                id: "${brand.id}",
                                ${brand.name ? 'name: "' + brand.name + '",' : ''}
                                ${brand.code ? 'code: "' + brand.code + '",' : ''}
                                ${brand.contentDistributionCategory ? 'contentDistributionCategory:' + brand.contentDistributionCategory + ',' : ''}
                                ${
                                    brand.contentDistributionRestrictionsInput
                                        ? 'contentDistributionRestrictionsInput: [' +
                                          brand.contentDistributionRestrictionsInput
                                              ?.filter((setting) => setting !== undefined)
                                              ?.map((id) => `{channelId:"${id}"}`) +
                                          '],'
                                        : ''
                                }
                                ${brand.visualsDistributionCategory ? 'visualsDistributionCategory:' + brand.visualsDistributionCategory + ',' : ''}
                                ${
                                    brand.visualsDistributionRestrictionsInput
                                        ? 'visualsDistributionRestrictionsInput: [' +
                                          brand.visualsDistributionRestrictionsInput
                                              ?.filter((setting) => setting !== undefined)
                                              ?.map((id) => `{channelId:"${id}"}`) +
                                          '],'
                                        : ''
                                }
                                ${
                                    brand.brandDefaultChannels
                                        ? 'brandDefaultChannelsInput: [ ' +
                                          brand.brandDefaultChannels
                                              ?.filter((setting) => setting !== undefined)
                                              ?.map((id) => `{channelId:"${id}"}`) +
                                          '],'
                                        : ''
                                }
                                ${brand.mappings ? 'brandMappingsInput: ' + JSON.stringify(brand.mappings)?.replace(/"([^"]+)":/g, '$1:') + ',' : ''}
                                ${brand.featureGroupIds ? 'brandFeatureGroupSettingsInput: [' + brand.featureGroupIds?.map((id) => `{featureGroupId: "${id}"}`) + '],' : ''}
                                ${brand.hotelCategories ? `brandFeatureDetailsInput:[{resourceName:"hotelCategory", resourceValues:[${brand.hotelCategories?.map((hc) => `"${hc}",`)}]}, {resourceName:"segment",resourceValues:["${brand.brandSegment}"]}]` : ''}
                                ${
                                    brand.brandViewSettings?.type === BrandRestrictiveUiType.DEFAULT
                                        ? 'brandViewSettingsInput: { type: DEFAULT },'
                                        : brand.brandViewSettings?.type === BrandRestrictiveUiType.RESTRICTIVE &&
                                            brand.brandViewSettings.brandViewSettingsChannelRestriction
                                          ? 'brandViewSettingsInput: { type: RESTRICTIVE, channelRestrictionsInput: [' +
                                            brand.brandViewSettings.brandViewSettingsChannelRestriction
                                                .filter((c) => c !== undefined)
                                                .map((c) => `{ channelId: "${c.channelId}" }`)
                                                .join(',') +
                                            '] },'
                                          : ''
                                }
                            }
                        ) {
                            statusCode,
                            message
                        }
                    }
                }
        `;

        return this.apollo
            .mutate({
                mutation: gql(brandMutation),
            })
            .pipe(
                map((response: any) => {
                    return response;
                }),
            );
    }

    public getBrandsSettings(): Observable<DynamicForm> {
        return this.http.get<DynamicForm>(BRAND_SETTINGS_ENDPOINT);
    }
}
